/* eslint-disable no-unreachable */
import React, { useCallback, useEffect, useState } from 'react';
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import ProgramSignal, {
  fundingProgramFormErrorsSignal,
} from 'signals/Program.signal';
import addProgramImg from 'assets/images/add-program.png';
import EdoAddProgramDocumentForm from './components/EdoAddProgramDocumentForm';
import BasicProgramInfo from './components/BasicProgramInfo';
import {
  addDocumentToBusinessChoices,
  addDocumentToLenderChoices,
  addDocumentToVcChoices,
  fetchAndSetAddProgramData,
  handleAddProgramFileRemoval,
  handleAddProgramFileUpdate,
  handleAddProgramStepSubmission,
  handleAddSuggestedDocument,
  handleAttachFile,
  handleDocumentUploadInputFocusOut,
  handleInputChange,
  handleInputFocusOut,
  NUM_OF_STEPS,
  prevStep,
  removeBusinessDocumentChoice,
  removeLenderDocumentChoice,
  removeVcDocumentChoice,
  resetFormFlow,
} from './EdoAddProgram.helpers';
import SuccessPage from './components/SuccessPage';
import { fetchAndSetPrograms } from '../EdoPrograms/EdoPrograms.helpers';
import { Prompt } from 'react-router-dom';
import ApprovalChainTemplating from 'components/global/ApprovalChainTemplating/ApprovalChainTemplating';
import approvalChainTemplatingSignal from 'components/global/ApprovalChainTemplating/ApprovalChainTemplating.signal';
import loaderSignal from 'signals/Loader.signal';
import { canPerformAction } from '../../../../libs/programAccessPermissions';
import { shouldHaveFullEdoAccess } from '../../../../libs/edoPermissions';
import userAccountSignal from '../../../../signals/UserAccount.signal';
import api from '../../../../api/api';

const STEP_TITLES = [
  '1. Basic Info',
  '2. Program Documents',
  '3. Approval Chain',
];

export const EdoAddProgram = () => {
  const { programInfo } = ProgramSignal.value;
  const [canInsert, setCanInsert] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Gate Add Program the same way other EDO screens do (edoPermissions).
  // Bug (Sep 2026 sandbox wipe/recreate): requiring canInsert on an *existing*
  // FundingProgram denied Admin/Owner when the EDO had zero programs (chicken
  // and egg after wipe). Full EDO access must be able to create the first one.
  // See Hotfix H2 / PR for this change; do not reintroduce empty-list deny
  // for shouldHaveFullEdoAccess users.
  useEffect(() => {
    const checkInsertPermission = async () => {
      try {
        loaderSignal.update({ isContentLoading: true });
        const userData = userAccountSignal.value.userData;

        if (shouldHaveFullEdoAccess(userData)) {
          setCanInsert(true);
          return;
        }

        const fundingPrograms = await api.get({
          path: '/fundingPrograms',
          options: {
            where: {
              edoId: userData?.account.edoId,
            },
          },
        });

        if (!fundingPrograms || fundingPrograms.length === 0) {
          setCanInsert(false);
          return;
        }

        const insertChecks = await Promise.all(
          fundingPrograms.map((program) =>
            canPerformAction(program.id, 'canInsert')
          )
        );

        setCanInsert(insertChecks.some((hasPermission) => hasPermission));
      } catch (error) {
        console.error('Error checking insert permission:', error);
        setCanInsert(false);
      } finally {
        setIsChecking(false);
        loaderSignal.reset();
      }
    };

    checkInsertPermission();
  }, []);

  const init = useCallback(async () => {
    if (canInsert) {
      await fetchAndSetAddProgramData();
      await fetchAndSetPrograms();
    }
  }, [canInsert]);

  useEffect(() => {
    if (canInsert) {
      ProgramSignal.reset();
      fundingProgramFormErrorsSignal.reset();
      approvalChainTemplatingSignal.reset();
      init();
    }
  }, [canInsert, init]);

  if (isChecking) {
    return (
      <Row>
        <Col className="mt-32">
          <Card className="bg-white mx-16 rounded-0">
            <Card.Body>
              <p className="text-center">Loading...</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  if (!canInsert) {
    return (
      <Row>
        <Col className="mt-32">
          <Card className="bg-white mx-16 rounded-0">
            <Card.Body>
              <h3 className="text-center">Access Denied</h3>
              <p className="text-center">
                You do not have permission to create new programs.
              </p>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    );
  }

  const renderStepContent = () => {
    const { step } = programInfo;
    switch (step) {
      case 1:
        return (
          <BasicProgramInfo
            handleInputChange={handleInputChange}
            programInfo={programInfo}
            programTypes={ProgramSignal.value.programTypes}
            platforms={ProgramSignal.value.platforms}
            administrators={ProgramSignal.value.administrators}
            creatingProgram={true}
            handleInputFocusOut={handleInputFocusOut}
          />
        );
      case 2:
        return (
          <EdoAddProgramDocumentForm
            handleAttachFile={handleAttachFile}
            programInfo={programInfo}
            creatingProgram
            handleFileRemoval={handleAddProgramFileRemoval}
            handleFileChange={handleAddProgramFileUpdate}
            handleAddLenderDocument={addDocumentToLenderChoices}
            handleAddBusinessDocument={addDocumentToBusinessChoices}
            handleAddVcDocument={addDocumentToVcChoices}
            handleRemoveBusinessDocument={removeBusinessDocumentChoice}
            handleRemoveLenderDocument={removeLenderDocumentChoice}
            handleRemoveVcDocument={removeVcDocumentChoice}
            programTypes={ProgramSignal.value.programTypes}
            handleDocumentUploadInputFocusOut={
              handleDocumentUploadInputFocusOut
            }
            handleAddSuggestedDocument={handleAddSuggestedDocument}
          />
        );
      case 3:
        return <ApprovalChainTemplating creatingProgram />;
      case 4:
        return <SuccessPage />;
      default:
        return ProgramSignal.update({ step: 1 });
    }
  };

  return (
    <>
      <Prompt
        when={programInfo.step < 4}
        message="You will lose all of your progress, are you sure you want to leave?"
      />
      <Row>
        <Col className="mt-32">
          <Row className="mb-8">
            <Col>
              <h5 className="text-secondary ml-16">
                {STEP_TITLES[programInfo.step - 1]}
              </h5>
            </Col>
            <Col>
              <Button
                variant="outline-secondary"
                className="float-right mr-16 rounded"
                hidden={programInfo.step > NUM_OF_STEPS}
                onClick={() => resetFormFlow()}
              >
                Reset
              </Button>
            </Col>
          </Row>
          <Row className="mb-32">
            <Col>
              <Card className="bg-white mx-16 rounded-0">
                <Card.Body>
                  <Row>
                    <Col xl={8}>
                      <Form onSubmit={() => console.log('onSubmit()')}>
                        {renderStepContent()}
                      </Form>
                      <Row className="my-64" hidden={programInfo.step === 4}>
                        <Col className="text-center">
                          <Button
                            className="rounded"
                            hidden={programInfo.step === 1}
                            onClick={prevStep}
                          >
                            Prev Page
                          </Button>
                        </Col>
                        <Col hidden={programInfo.step > NUM_OF_STEPS}>
                          <p className="text-center">{`${programInfo.step}/${NUM_OF_STEPS}`}</p>
                        </Col>
                        <Col className="text-center">
                          <Button
                            className="rounded"
                            disabled={loaderSignal.value.isContentLoading}
                            onClick={() => handleAddProgramStepSubmission()}
                          >
                            {programInfo.step === 3
                              ? 'Create Program'
                              : 'Next Page'}
                          </Button>
                        </Col>
                      </Row>
                    </Col>
                    <Col>
                      <img
                        className="img-fluid"
                        src={addProgramImg}
                        alt="MstreetX logo"
                      />
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Col>
      </Row>
    </>
  );
};

export default EdoAddProgram;
